import { useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, BookMarked, FolderKanban, Camera, FolderOpen, LogOut, Sparkles, Crown, Settings as SettingsIcon, Brain, Video, CalendarClock, LifeBuoy, Menu, X, Inbox, Users, Gift, Gem, Wrench, Megaphone, Zap, Tv, FileText } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import PwaInstallPrompt from "./PwaInstallPrompt";
import FreeTrialPopup from "./FreeTrialPopup";
import PasskeyPrompt from "./PasskeyPrompt";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/glossary", label: "Glossary", icon: BookMarked, testid: "nav-glossary" },
  { to: "/categories", label: "Categories", icon: FolderKanban, testid: "nav-categories" },
  { to: "/scan", label: "Photo Scan", icon: Camera, testid: "nav-scan" },
  { to: "/slides", label: "Slides → Notes", icon: FileText, testid: "nav-slides" },
  { to: "/quiz", label: "Quiz", icon: Brain, testid: "nav-quiz" },
  { to: "/tests", label: "Tests", icon: CalendarClock, testid: "nav-tests" },
  { to: "/materials", label: "Materials", icon: FolderOpen, testid: "nav-materials" },
  { to: "/drafts", label: "AI Drafts", icon: Sparkles, testid: "nav-drafts" },
  { to: "/pricing", label: "Pricing", icon: Crown, testid: "nav-pricing" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" },
];

const ADMIN_NAV = [
  { to: "/admin/users", label: "Users", icon: Users, testid: "nav-admin-users" },
  { to: "/admin/broadcast", label: "Broadcast", icon: Megaphone, testid: "nav-admin-broadcast" },
  { to: "/admin/automation", label: "Automation", icon: Zap, testid: "nav-admin-automation" },
  { to: "/admin/codes", label: "Gift codes", icon: Gift, testid: "nav-admin-codes" },
  { to: "/admin/free-trial", label: "Free trial", icon: Gem, testid: "nav-admin-free-trial" },
  { to: "/admin/maintenance", label: "Maintenance", icon: Wrench, testid: "nav-admin-maintenance" },
  { to: "/admin/landing-video", label: "Landing Video", icon: Video, testid: "nav-admin-video" },
  { to: "/admin/support", label: "Support inbox", icon: Inbox, testid: "nav-admin-support" },
];

function NavItem({ to, label, icon: Icon, testid, onClick, accent = "yellow" }) {
  const activeBg = accent === "pink" ? "bg-pink-300" : "bg-yellow-300";
  return (
    <NavLink
      to={to}
      data-testid={testid}
      onClick={onClick}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 rounded-md font-bold border-2 transition-all ${
          isActive
            ? `${activeBg} border-slate-900 shadow-[3px_3px_0_0_#0F172A]`
            : "bg-white border-transparent hover:border-slate-900 hover:shadow-[3px_3px_0_0_#0F172A]"
        }`
      }
    >
      <Icon className="w-5 h-5" strokeWidth={2.5} />
      <span>{label}</span>
    </NavLink>
  );
}

function NavList({ user, onItemClick }) {
  return (
    <>
      <nav className="flex flex-col gap-2" data-testid="sidebar-nav">
        {NAV.map((n) => <NavItem key={n.to} {...n} onClick={onItemClick} />)}
        {user?.is_admin && (
          <>
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mt-4 mb-1 px-3">Admin</div>
            {ADMIN_NAV.map((n) => <NavItem key={n.to} {...n} onClick={onItemClick} accent="pink" />)}
          </>
        )}
        <Link to="/support" onClick={onItemClick} data-testid="nav-support" className="flex items-center gap-3 px-3 py-2.5 mt-4 rounded-md font-bold border-2 border-transparent bg-slate-100 hover:border-slate-900 transition-all">
          <LifeBuoy className="w-5 h-5" strokeWidth={2.5} />
          <span>Contact support</span>
        </Link>
      </nav>
    </>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const onLogout = async () => {
    await logout();
    setMobileOpen(false);
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-64 flex-col border-r-2 border-slate-900 bg-white p-5 gap-6 sticky top-0 h-screen overflow-y-auto">
        <Link to="/dashboard" className="flex items-center gap-2">
          <div className="w-10 h-10 bg-yellow-300 border-2 border-slate-900 rounded-md grid place-items-center" style={{ boxShadow: "3px 3px 0 0 #0F172A" }}>
            <BookMarked className="w-5 h-5" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-display text-lg font-black leading-none">Studict</div>
            <div className="text-xs font-medium text-slate-500">Smart student dictionary</div>
          </div>
        </Link>

        <NavList user={user} />

        <div className="mt-auto flex flex-col gap-3">
          <div className="nb-card-sm p-3 flex items-center gap-3">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-9 h-9 rounded-full border-2 border-slate-900" />
            ) : (
              <div className="w-9 h-9 rounded-full border-2 border-slate-900 bg-green-300" />
            )}
            <div className="min-w-0">
              <div className="font-bold text-sm truncate" data-testid="user-name">{user?.name}</div>
              <div className="text-xs text-slate-500 truncate">{user?.email}</div>
            </div>
          </div>
          <button onClick={onLogout} data-testid="logout-btn" className="nb-btn-secondary inline-flex items-center justify-center gap-2">
            <LogOut className="w-4 h-4" strokeWidth={2.5} /> Logout
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="md:hidden fixed top-0 inset-x-0 z-30 bg-white border-b-2 border-slate-900 px-4 py-3 flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center gap-2" onClick={() => setMobileOpen(false)}>
          <div className="w-9 h-9 bg-yellow-300 border-2 border-slate-900 rounded-md grid place-items-center" style={{ boxShadow: "2px 2px 0 0 #0F172A" }}>
            <BookMarked className="w-4 h-4" strokeWidth={2.5} />
          </div>
          <span className="font-display text-lg font-black">Studict</span>
        </Link>
        <button onClick={() => setMobileOpen(true)} aria-label="Open menu" data-testid="mobile-menu-btn" className="nb-btn-secondary !py-2 !px-3 inline-flex items-center gap-1.5 text-sm">
          <Menu className="w-4 h-4" strokeWidth={2.5}/> Menu
        </button>
      </header>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-slate-900/50" onClick={() => setMobileOpen(false)}>
          <aside className="absolute right-0 top-0 bottom-0 w-[85%] max-w-sm bg-white border-l-2 border-slate-900 p-5 overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="mobile-drawer">
            <div className="flex items-center justify-between mb-5">
              <span className="font-display text-xl font-black">Menu</span>
              <button onClick={() => setMobileOpen(false)} aria-label="Close menu" className="nb-btn-secondary !py-2 !px-2">
                <X className="w-4 h-4" strokeWidth={2.5}/>
              </button>
            </div>
            <NavList user={user} onItemClick={() => setMobileOpen(false)} />
            <div className="mt-6 nb-card-sm p-3 flex items-center gap-3">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-9 h-9 rounded-full border-2 border-slate-900" />
              ) : (
                <div className="w-9 h-9 rounded-full border-2 border-slate-900 bg-green-300" />
              )}
              <div className="min-w-0">
                <div className="font-bold text-sm truncate">{user?.name}</div>
                <div className="text-xs text-slate-500 truncate">{user?.email}</div>
              </div>
            </div>
            <button onClick={onLogout} data-testid="logout-btn-mobile" className="nb-btn-secondary mt-4 w-full inline-flex items-center justify-center gap-2">
              <LogOut className="w-4 h-4" strokeWidth={2.5}/> Logout
            </button>
          </aside>
        </div>
      )}

      <main className="flex-1 min-w-0 pt-14 md:pt-0">
        <Outlet />
      </main>

      <PwaInstallPrompt />
      <FreeTrialPopup />
      <PasskeyPrompt />
    </div>
  );
}
